import { useRef, useState } from "react";
import type { ReactNode } from "react";
import type { MediaItem } from "@gooner/shared";
import { ClipTile } from "../control/components/ClipTile";
import { ClipEditModal } from "../control/components/ClipEditModal";
import { AddToCollection } from "../control/components/AddToCollection";
import { FapCrownButton, type FapCrownSurface } from "../control/components/FapCrownButton";
import { IconCheck } from "../control/ui/icons";
import { cx } from "../control/ui/primitives";
import { useLongPress } from "../lib/useLongPress";

/**
 * THE clip thumbnail, everywhere a clip is shown to be browsed — Browse, every collection
 * (public and your own), profile highlights. One card so a clip offers the same three
 * things wherever it turns up: the details popup, the crown, and add-to-collection. A
 * surface's own buttons (a collection's star and trash) go in `extraActions`, along the
 * bottom; its own label goes in `badge`.
 *
 * A click (a tap) anywhere on the picture opens the popup — the buttons stop their own
 * clicks, so the crown, "+" and the extras never open it. There is no visible View button:
 * once one click opened the popup it was a second way to do the same thing. It stays for
 * the keyboard only (the card itself takes no focus), invisible until tabbed to.
 *
 * Pickers do NOT use this: a tap there selects, and buttons on top of a selection target
 * fight it. They keep the bare ClipTile.
 *
 * The card owns its popup. `list` is what the popup's ‹ › page through — pass the grid the
 * card sits in.
 *
 * `select` (a collection page's bulk select, issue #12) makes it selectable. Off, a mouse
 * gets a checkbox on hover and a finger gets a long-press, either of which starts select
 * mode with this card picked. On, a click anywhere on the card toggles it (Shift-click
 * for a range) and the crown, "+" and extras are gone: the same rule as the pickers,
 * a tap selects, so nothing on top of the card may mean something else.
 */
/** Longer than a double-click's gap (~300ms, DisplayApp's DOUBLE_TAP_MS), short enough
 *  that no one closes a popup on purpose inside it. */
const DOUBLE_CLICK_GRACE_MS = 400;

export function ClipCard({
  clip,
  list,
  surface,
  badge,
  extraActions,
  select,
  eager,
}: {
  clip: MediaItem;
  list?: MediaItem[];
  surface: FapCrownSurface;
  badge?: ReactNode;
  extraActions?: ReactNode;
  select?: {
    active: boolean;
    selected: boolean;
    /** `range`: Shift was held. */
    onToggle: (range: boolean) => void;
    /** Start select mode with this card picked. */
    onEnter: (entry: "checkbox" | "long_press") => void;
  };
  /** Above the fold: load the poster now instead of lazily (ClipTile). */
  eager?: boolean;
}) {
  const [viewing, setViewing] = useState(false);
  // The popup closes on a press on its backdrop, and it opens on the FIRST click — so a
  // double-click, the gesture that opened it before one click did, would open it and then
  // close it on the second press. A close that lands inside that window is that second
  // press, not a decision.
  const openedAt = useRef(0);
  const open = () => {
    openedAt.current = performance.now();
    setViewing(true);
  };
  const close = () => {
    if (performance.now() - openedAt.current < DOUBLE_CLICK_GRACE_MS) return;
    setViewing(false);
  };

  const longPress = useLongPress(() => {
    if (select?.active) select.onToggle(false);
    else select?.onEnter("long_press");
  }, !!select);
  const selecting = !!select?.active;

  const checkbox = select ? (
    <button
      type="button"
      role="checkbox"
      aria-checked={select.selected}
      aria-label={select.selected ? "Deselect clip" : "Select clip"}
      onClick={(e) => {
        e.stopPropagation();
        if (selecting) select.onToggle(e.shiftKey);
        else select.onEnter("checkbox");
      }}
      className={cx(
        "flex h-6 w-6 items-center justify-center rounded-md border-2 transition-opacity",
        select.selected
          ? "border-[var(--color-accent)] bg-[var(--color-accent)] text-black"
          : "border-white/80 bg-black/50 text-transparent",
        // Off: a mouse reveals it on hover (or a Tab focuses it); a finger long-presses.
        !selecting && "opacity-0 focus-visible:opacity-100 group-hover:opacity-100 pointer-coarse:hidden",
      )}
    >
      <IconCheck width={14} height={14} />
    </button>
  ) : null;

  const tile = (
    <ClipTile
      clip={clip}
      eager={eager}
      onClick={selecting ? (e) => select!.onToggle(e.shiftKey) : open}
      badge={
        checkbox || badge ? (
          <div className="flex items-center gap-1">
            {checkbox}
            {badge}
          </div>
        ) : undefined
      }
      topRight={
        selecting ? undefined : (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={open}
              className="sr-only focus-visible:not-sr-only focus-visible:flex focus-visible:h-6 focus-visible:items-center focus-visible:rounded-md focus-visible:bg-[var(--color-accent)] focus-visible:px-2 focus-visible:text-[10px] focus-visible:text-white"
            >
              View
            </button>
            <FapCrownButton clip={clip} surface={surface} />
            {/* Uploads too, since 2026-10-28: the menu lists only private collections for them. */}
            <AddToCollection clip={clip} />
          </div>
        )
      }
      actions={selecting ? undefined : extraActions}
    />
  );

  return (
    <>
      {select ? (
        // The ring and the long-press ride a wrapper so ClipTile stays the bare tile. No
        // text selection or iOS image callout under a held finger.
        <div
          {...longPress}
          className={cx(
            "select-none rounded-lg [-webkit-touch-callout:none]",
            select.selected && "ring-2 ring-[var(--color-accent)] ring-offset-2 ring-offset-[var(--color-ink)]",
          )}
        >
          {tile}
        </div>
      ) : (
        tile
      )}
      {viewing ? <ClipEditModal clip={clip} list={list} onClose={close} /> : null}
    </>
  );
}
